// Painel de Processadores do merchant: as CREDENCIAIS, não os meios de pagamento.
//
// A separação é o ponto da tela. Antes, cadastrar um meio de pagamento criava uma credencial
// nova — um merchant Itaú com PIX + boleto + bolepix acabava com três cópias das mesmas
// credenciais, e nenhuma delas podia servir outro white label do mesmo merchant.
//
// O layout é lista agrupada por banco, e não grid de cards: um merchant com oito credenciais
// Itaú viraria uma parede de cartões idênticos, com validade do certificado e dependentes — o
// que decide ação — todos no mesmo peso visual.
import {
  PlusIcon,
  SearchIcon,
  ServerIcon,
  ServerOffIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useNavigate,
  useSearchParams,
} from "react-router-dom"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import { useWhiteLabels } from "@/hooks/use-white-labels"
import {
  useDeleteMerchantProcessor,
  useMerchantProcessors,
} from "@/services/admin/merchant-processors"
import { useProcessorConfigMetadata } from "@/services/admin/processor-config"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"

import {
  countExpiringProcessors,
  EMPTY_PROCESSOR_FILTERS,
  filterProcessors,
  groupProcessors,
  groupsOpenByDefault,
  needsAttention,
  type ProcessorFilters,
} from "../utils"
import { CreateMerchantProcessorDialog } from "./create-merchant-processor-dialog"
import { MerchantProcessorRow } from "./merchant-processor-row"

const prefix = "pages.adminMerchantProcessors"

// Skeleton fiel ao layout: barra de busca + dois grupos com linhas.
const PanelSkeleton = () => (
  <div
    data-testid="merchant-processors-skeleton"
    className="space-y-4"
  >
    <Skeleton className="h-10 w-full rounded-md" />
    {Array.from({ length: 2 }).map((_, group) => (
      <div
        key={`processor-skeleton-${group}`}
        className="space-y-2"
      >
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-16 w-full rounded-lg" />
      </div>
    ))}
  </div>
)

export const MerchantProcessorsPanel = () => {
  const { t } = useTranslation()

  const {
    data: processors,
    isLoading,
    isError,
    refetch,
  } = useMerchantProcessors()
  const { data: metadata } = useProcessorConfigMetadata()
  // Só para traduzir id → nome: `WHL-6fd247c6…` na linha não diz nada a ninguém.
  const { data: whiteLabels } = useWhiteLabels()

  // Filtro na URL, como no resto do admin: sobrevive a refresh e o operador
  // consegue mandar o link já filtrado para quem vai agir.
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters: ProcessorFilters = {
    search:
      searchParams.get("q") ??
      EMPTY_PROCESSOR_FILTERS.search,
    gateway: searchParams.get("gateway"),
  }

  const setFilter = (
    key: "q" | "gateway",
    value: string | null
  ) => {
    const next = new URLSearchParams(searchParams)
    if (value) {
      next.set(key, value)
    } else {
      next.delete(key)
    }
    setSearchParams(next, { replace: true })
  }
  const [creating, setCreating] = useState(false)
  const [pendingDelete, setPendingDelete] =
    useState<MerchantProcessor | null>(null)

  const { mutate: remove } = useDeleteMerchantProcessor(
    () => setPendingDelete(null)
  )

  const gatewayLabels = useMemo(() => {
    const labels = new Map<string, string>()
    for (const item of metadata ?? []) {
      labels.set(item.gateway, item.label)
    }
    return labels
  }, [metadata])

  const whiteLabelNames = useMemo(() => {
    const names = new Map<string, string>()
    for (const item of Array.isArray(whiteLabels)
      ? whiteLabels
      : []) {
      names.set(item.uuid, item.name)
    }
    return names
  }, [whiteLabels])

  const list = useMemo(() => processors ?? [], [processors])
  const groups = useMemo(
    () => groupProcessors(filterProcessors(list, filters)),
    [list, filters]
  )
  const hasActiveFilters =
    filters.search.trim().length > 0 ||
    filters.gateway !== null
  // Conta sobre TODAS as credenciais, não sobre as filtradas: o aviso é do
  // merchant, e sumiria só por haver uma busca aberta.
  const expiringCount = countExpiringProcessors(list)

  const gatewaysInUse = useMemo(
    () =>
      [
        ...new Set(
          list.map((processor) => processor.gateway)
        ),
      ].sort(),
    [list]
  )

  if (isLoading) return <PanelSkeleton />

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center">
        <ServerOffIcon
          className="size-8 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">
          {t(`${prefix}.loadError`)}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          {t(`${prefix}.retry`)}
        </Button>
      </div>
    )
  }

  if (list.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center">
          <ServerIcon
            className="size-8 text-muted-foreground"
            aria-hidden="true"
          />
          <div className="space-y-1">
            <p className="font-medium">
              {t(`${prefix}.emptyTitle`)}
            </p>
            <p className="text-sm text-muted-foreground">
              {t(`${prefix}.emptyState`)}
            </p>
          </div>
          <Button onClick={() => setCreating(true)}>
            <PlusIcon
              className="mr-2 size-4"
              aria-hidden="true"
            />
            {t(`${prefix}.create.cta`)}
          </Button>
        </div>
        <CreateMerchantProcessorDialog
          open={creating}
          onOpenChange={setCreating}
          metadata={metadata ?? []}
        />
      </>
    )
  }

  return (
    <div className="space-y-4">
      {/* Só aparece quando há o que revisar — banner permanente vira paisagem. */}
      {expiringCount > 0 && (
        <div
          data-testid="processors-expiring-banner"
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          <TriangleAlertIcon
            className="size-4 shrink-0"
            aria-hidden="true"
          />
          {t(`${prefix}.expiringBanner`, {
            count: expiringCount,
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <SearchIcon
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={filters.search}
            className="pl-9"
            aria-label={t(`${prefix}.searchAria`)}
            placeholder={t(`${prefix}.searchPlaceholder`)}
            onChange={(event) =>
              setFilter("q", event.target.value)
            }
          />
        </div>

        {/* O filtro só existe quando há mais de um banco para filtrar. */}
        {gatewaysInUse.length > 1 && (
          <Select
            value={filters.gateway ?? "ALL"}
            onValueChange={(value) =>
              setFilter(
                "gateway",
                value === "ALL" ? null : value
              )
            }
          >
            <SelectTrigger
              className="w-52"
              aria-label={t(`${prefix}.gatewayFilterAria`)}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">
                {t(`${prefix}.allGateways`)}
              </SelectItem>
              {gatewaysInUse.map((gateway) => (
                <SelectItem key={gateway} value={gateway}>
                  {gatewayLabels.get(gateway) ?? gateway}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Button onClick={() => setCreating(true)}>
          <PlusIcon
            className="mr-2 size-4"
            aria-hidden="true"
          />
          {t(`${prefix}.create.cta`)}
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center">
          <SearchIcon
            className="size-8 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="text-sm text-muted-foreground">
            {t(`${prefix}.emptyFiltered`)}
          </p>
          <Button
            variant="outline"
            onClick={() =>
              setSearchParams({}, { replace: true })
            }
          >
            {t(`${prefix}.clearFilters`)}
          </Button>
        </div>
      ) : (
        <Accordion
          type="multiple"
          // Remonta quando o conjunto muda.
          key={groups
            .map((group) => group.gateway)
            .join("|")}
          // Com filtro ativo, TODOS abertos: o Radix desmonta o conteúdo fechado, e um
          // grupo urgente bastaria para esconder o resultado que o operador acabou de
          // buscar. Sem filtro, só o que pede ação.
          defaultValue={
            hasActiveFilters
              ? groups.map((group) => group.gateway)
              : groupsOpenByDefault(groups)
          }
          className="space-y-2"
        >
          {groups.map((group) => (
            <AccordionItem
              key={group.gateway}
              value={group.gateway}
              data-testid={`processor-group-${group.gateway}`}
              className="rounded-xl border bg-card px-4 shadow-sm"
            >
              <AccordionTrigger className="hover:no-underline">
                <span className="flex flex-1 flex-wrap items-center justify-between gap-2 pr-2 text-left">
                  <span className="text-sm font-semibold">
                    {gatewayLabels.get(group.gateway) ??
                      group.gateway}
                  </span>
                  <span className="flex items-center gap-2">
                    {/* O aviso fica no cabeçalho, e não só dentro do grupo: com o
                        grupo fechado, um certificado vencido some da tela. */}
                    {group.processors.some(
                      needsAttention
                    ) && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        <TriangleAlertIcon
                          className="size-3"
                          aria-hidden="true"
                        />
                        {t(`${prefix}.groupNeedsAttention`)}
                      </span>
                    )}
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {t(`${prefix}.groupCount`, {
                        count: group.processors.length,
                      })}
                    </span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2 pb-2">
                  {group.processors.map((processor) => (
                    <MerchantProcessorRow
                      key={processor.id}
                      processor={processor}
                      whiteLabelName={(id) =>
                        whiteLabelNames.get(id) ?? id
                      }
                      onOpen={(item) =>
                        navigate(
                          FRONTEND_ROUTES.ADMIN_MERCHANT_PROCESSOR_DETAIL.href.replace(
                            ":processorId",
                            item.id
                          )
                        )
                      }
                      onDelete={setPendingDelete}
                    />
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      <CreateMerchantProcessorDialog
        open={creating}
        onOpenChange={setCreating}
        metadata={metadata ?? []}
      />

      <AlertDialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${prefix}.delete.title`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(`${prefix}.delete.description`, {
                name: pendingDelete?.name ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${prefix}.delete.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) remove(pendingDelete.id)
              }}
            >
              {t(`${prefix}.delete.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
